import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetPurchaseOrdersQuery, useDeletePurchaseOrderMutation } from "../../../store/api/purchaseApi";
import { useGetCurrentCompanyQuery } from "../../../store/api/companyApi";

function PurchaseOrdersList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const toApiDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate(),
          10,
        ).padStart(2, "0")}`
      : undefined;
  const [deleteDialogOrder, setDeleteDialogOrder] = useState(null);
  const [deletePurchaseOrder, { isLoading: isDeleting }] = useDeletePurchaseOrderMutation();
  const { data, isLoading, error } = useGetPurchaseOrdersQuery({
    page,
    limit: rowsPerPage,
    status: statusFilter || undefined,
    search: search || undefined,
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
  });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || "USD";
  const dateFormat = company?.dateFormat || "dd/MM/yyyy";

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case "MM/dd/yyyy":
        return `${mm}/${dd}/${yyyy}`;
      case "yyyy-MM-dd":
        return `${yyyy}-${mm}-${dd}`;
      case "dd-MM-yyyy":
        return `${dd}-${mm}-${yyyy}`;
      case "dd/MM/yyyy":
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      pending: "warning",
      approved: "info",
      received: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error)
    return <Typography color="error">Error loading purchase orders</Typography>;

  const orders = data?.purchaseOrders || [];
  const pagination = data?.pagination;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Purchases
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/purchases/order-notes")}
            sx={{ borderRadius: 2 }}
          >
            Purchase Orders (PO)
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/purchases/returns")}
            sx={{ borderRadius: 2 }}
          >
            Returns
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/purchases/suppliers")}
            sx={{ borderRadius: 2 }}
          >
            Suppliers
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/purchases/orders/new")}
            sx={{ borderRadius: 2 }}
          >
            New Purchase
          </Button>
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card>
          <CardContent>
            <Box
            sx={{
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <TextField
                size="small"
                label="Search (order / invoice / LPO)"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 220 }}
              />
              <TextField
                size="small"
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="received">Received</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
              <DatePicker
                label="From date"
                value={fromDate}
                onChange={(newValue) => {
                  setFromDate(newValue);
                  setPage(1);
                }}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="To date"
                value={toDate}
                onChange={(newValue) => {
                  setToDate(newValue);
                  setPage(1);
                }}
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
            {pagination && (
              <Typography variant="body2" color="text.secondary">
                {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
              </Typography>
            )}
          </Box>

            <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>LPO Ref.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                  <TableCell
                    sx={{ fontWeight: 600 }}
                  >{`Total Amount (${currency})`}</TableCell>
                  <TableCell
                    sx={{ fontWeight: 600 }}
                  >{`Credit Balance (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {order.orderNumber}
                      </TableCell>
                      <TableCell>{order.invoiceNumber || "–"}</TableCell>
                      <TableCell>{order.lpoNumber || "–"}</TableCell>
                      <TableCell>{order.supplier?.name || "-"}</TableCell>
                      <TableCell>{formatDate(order.orderDate)}</TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {formatAmount(order.totalAmount)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color:
                            order.purchaseType === "credit" &&
                            Number(order.balanceAmount) > 0
                              ? "warning.main"
                              : "text.secondary",
                        }}
                      >
                        {order.purchaseType === "credit"
                          ? formatAmount(order.balanceAmount)
                          : "–"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status}
                          color={getStatusColor(order.status)}
                          size="small"
                          sx={{ textTransform: "capitalize", fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            navigate(`/purchases/orders/${order.id}`)
                          }
                          title="View"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialogOrder(order);
                          }}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No purchase orders found
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/purchases/orders/new")}
                        sx={{ mt: 2 }}
                      >
                        Create First Purchase Order
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
            {pagination && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
              </Typography>
              <TablePagination
                component="div"
                count={pagination.total}
                page={(pagination.page || 1) - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                rowsPerPage={pagination.limit || rowsPerPage}
                onRowsPerPageChange={(e) => {
                  const next = parseInt(e.target.value, 10) || 10;
                  setRowsPerPage(next);
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page:"
              />
            </Box>
            )}
          </CardContent>
        </Card>
      </LocalizationProvider>

      <Dialog
        open={!!deleteDialogOrder}
        onClose={() => setDeleteDialogOrder(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete purchase order?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the purchase order and reverse stock and accounting entries. This action cannot be undone.
          </Typography>
          {deleteDialogOrder && (
            <Typography sx={{ mt: 1, fontWeight: 600 }}>
              Order: {deleteDialogOrder.orderNumber}
              {deleteDialogOrder.invoiceNumber ? ` · Invoice: ${deleteDialogOrder.invoiceNumber}` : ""}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteDialogOrder(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting}
            onClick={async () => {
              if (!deleteDialogOrder) return;
              try {
                await deletePurchaseOrder(deleteDialogOrder.id).unwrap();
                setDeleteDialogOrder(null);
              } catch (err) {
                alert(err?.data?.message || "Failed to delete purchase order");
              }
            }}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PurchaseOrdersList;
